import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandIcon } from "@/design/primitives";
import { StatusBand } from "@/shell/StatusBand";
import { MusicPlayerProvider } from "@/music/MusicPlayer";
import { MusicPlayerBar } from "@/music/MusicPlayerBar";
import { IdeaCapture } from "@/screens/idea/IdeaCapture";
import { DeskFlashContext } from "@/shell/DeskFlashContext";

// ── Navigation model: portfolio (top tier) + brands (accordion) ──
// Fixed manual order per ratified proposal §3.
const PORTFOLIO_ITEMS = [
  // Jeff 10/30: the Desk and the Goal Board ARE the system — they lead the
  // menu; Needs Me/In Flight come off the menu (they stay routed at their
  // paths for links and muscle memory, just not menu entries).
  { key: "desk", label: "Desk", path: "/desk" },
  // Jeff 10/30: Desk 2 — the Desk as it stood, duplicated for his A/B while he
  // evaluates the reordered build. He names what ships when he's ready.
  { key: "desk2", label: "Desk 2", path: "/desk2" },
  // Chat (Jeff 2026-11-03): directly under Desk — his running conversation with Kit.
  { key: "chat", label: "Chat", path: "/chat" },
  { key: "goals", label: "Goal Board", path: "/goals" },
  { key: "schedule", label: "Schedule", path: "/schedule" },
  { key: "email", label: "Email", path: "/email" },
  { key: "phone", label: "Phone", path: "/phone" }, // the call loop (Jeff 2026-10-22)
  // Documents (P1): the workspace's own markdown and PDFs, searchable and
  // readable in the app. Read-only.
  { key: "docs", label: "Documents", path: "/docs" },
  // Music (phase 2-3): the daily set as a library. The player bar rides the
  // shell, so the music survives every navigation.
  { key: "music", label: "Music", path: "/music" },
  // Calendar folded into Schedule (Jeff 2026-10-21): /schedule → tab bar with
  // Calendar + Automation; /calendar redirects to /schedule?tab=calendar.
  // Cities removed from main menu (Jeff 2026-10-21): the city-projects board
  // lives only under True Joy Birthing → /tjb/cities.
  // Brands board removed from menu (Jeff 2026-10-21); overviews live in the
  // brand accordion below.
];

const BRAND_GROUPS = [
  {
    key: "tjb",
    label: "True Joy Birthing",
    slug: "truejoybirthing",
    pages: [
      { label: "Overview", path: "/tjb" },
      { label: "Cities", path: "/tjb/cities" },
      { label: "Calendar", path: "/tjb/calendar" },
      { label: "People", path: "/tjb/people" },
    ],
  },
  {
    key: "at",
    label: "AgenticTrust",
    slug: "agentictrust",
    pages: [
      { label: "Overview", path: "/at" },
      { label: "Email", path: "/at/email" },
      { label: "Calendar", path: "/at/calendar" },
      { label: "People", path: "/at/people" },
    ],
  },
  {
    key: "to",
    label: "TrustOffice",
    slug: "trustoffice",
    pages: [
      { label: "Overview", path: "/to" },
      { label: "Calendar", path: "/to/calendar" },
      { label: "People", path: "/to/people" },
    ],
  },
  {
    key: "av",
    label: "AeriusView",
    slug: "aeriusview",
    pages: [
      { label: "Overview", path: "/av" },
      { label: "Calendar", path: "/av/calendar" },
      { label: "People", path: "/av/people" },
    ],
  },
  {
    key: "wp",
    label: "WingPoint",
    slug: "wingpoint",
    pages: [
      { label: "Overview", path: "/wp" },
      { label: "Calendar", path: "/wp/calendar" },
      { label: "People", path: "/wp/people" },
    ],
  },
  {
    key: "tm",
    label: "TrustMinutes",
    slug: "trustminutes",
    pages: [
      { label: "Overview", path: "/tm" },
      { label: "Calendar", path: "/tm/calendar" },
      { label: "People", path: "/tm/people" },
    ],
  },
  {
    key: "sv",
    label: "SocializeVideo",
    slug: "socializevideo",
    pages: [
      { label: "Overview", path: "/sv" },
      { label: "Calendar", path: "/sv/calendar" },
      { label: "People", path: "/sv/people" },
    ],
  },
  {
    key: "sd",
    label: "StenoDesk",
    slug: "stenodesk",
    pages: [
      { label: "Overview", path: "/sd" },
      { label: "Calendar", path: "/sd/calendar" },
      { label: "People", path: "/sd/people" },
    ],
  },
];

// ── Helpers ──

const isActivePath = (pathname, item) => {
  if (!item.path) return false;
  if (item.key === "desk")
    return (
      pathname === "/" ||
      pathname === "/desk" ||
      pathname === "/home" ||
      pathname === "/kit"
    );
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
};

const isBrandGroupActive = (pathname, brandKey) => {
  // TJB is special: its pages live under /tjb/*
  const prefix = `/${brandKey}`;
  if (brandKey === "tjb") {
    return (
      pathname.startsWith("/tjb") || pathname.startsWith("/truejoybirthing")
    );
  }
  // For others, match canonical prefix or full-slug prefix
  const fullSlug = BRAND_GROUPS.find((b) => b.key === brandKey)?.slug;
  return (
    pathname.startsWith(prefix) ||
    (fullSlug && pathname.startsWith(`/${fullSlug}`))
  );
};

// ── Portfolio links (top tier) ──
const PortfolioLinks = ({ pathname, onNavigate, dataPrefix }) => (
  <nav className="flex flex-col gap-1" aria-label="Portfolio menu">
    {PORTFOLIO_ITEMS.map((item) =>
      item.soon ? (
        <span
          key={item.key}
          data-testid={`${dataPrefix}-soon-${item.key}`}
          aria-disabled="true"
          className="flex h-10 cursor-not-allowed items-center justify-between rounded-control px-3 text-[0.875rem] text-os-ink-3"
        >
          {item.label}
          <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
            soon
          </span>
        </span>
      ) : (
        <NavLink
          key={item.key}
          to={item.path}
          onClick={onNavigate}
          data-testid={`${dataPrefix}-${item.key}`}
          aria-current={isActivePath(pathname, item) ? "page" : undefined}
          className={({ isActive }) =>
            cn(
              "flex h-10 items-center rounded-control px-3 text-[0.875rem]",
              isActive || isActivePath(pathname, item)
                ? "bg-os-wash font-medium text-os-ink"
                : "text-os-ink-2 hover:bg-os-wash hover:text-os-ink",
            )
          }
        >
          {item.label}
        </NavLink>
      ),
    )}
  </nav>
);

// ── Brand accordion ──
const BrandAccordion = ({
  pathname,
  onNavigate,
  dataPrefix,
  openKey,
  setOpenKey,
}) => {
  const handleToggle = (key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  return (
    <nav className="flex flex-col gap-1" aria-label="Brands menu">
      <div className="px-3 pb-1 pt-3 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
        Brands
      </div>
      {BRAND_GROUPS.map((brand) => {
        const isOpen = openKey === brand.key;
        const groupActive = isBrandGroupActive(pathname, brand.key);
        return (
          <div
            key={brand.key}
            data-testid={`${dataPrefix}-brand-${brand.key}`}
            className="flex flex-col"
          >
            <button
              type="button"
              data-testid={`${dataPrefix}-brand-toggle-${brand.key}`}
              onClick={() => handleToggle(brand.key)}
              aria-expanded={isOpen}
              className={cn(
                "flex h-9 w-full items-center justify-between rounded-control px-3 text-left text-[0.8125rem] transition-colors",
                groupActive
                  ? "bg-os-wash font-medium text-os-ink"
                  : "text-os-ink-2 hover:bg-os-wash hover:text-os-ink",
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <BrandIcon brand={brand.label} size={18} />
                <span className="truncate">{brand.label}</span>
              </span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 shrink-0 text-os-ink-3 transition-transform duration-200",
                  isOpen && "rotate-180",
                )}
              />
            </button>
            {isOpen && (
              <div className="flex flex-col gap-0.5 py-1 pl-3">
                {brand.pages.map((page) =>
                  page.soon ? (
                    <span
                      key={page.label}
                      data-testid={`${dataPrefix}-brand-page-soon-${brand.key}-${page.label}`}
                      className="flex h-8 cursor-not-allowed items-center justify-between rounded-control px-3 text-[0.8125rem] text-os-ink-3"
                    >
                      {page.label}
                      <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
                        soon
                      </span>
                    </span>
                  ) : (
                    <NavLink
                      key={page.label}
                      to={page.path}
                      onClick={onNavigate}
                      data-testid={`${dataPrefix}-brand-page-${brand.key}-${page.label}`}
                      aria-current={pathname === page.path ? "page" : undefined}
                      className={({ isActive }) =>
                        cn(
                          "flex h-8 items-center rounded-control px-3 text-[0.8125rem]",
                          isActive || pathname === page.path
                            ? "bg-os-wash font-medium text-os-ink"
                            : "text-os-ink-2 hover:bg-os-wash hover:text-os-ink",
                        )
                      }
                    >
                      {page.label}
                    </NavLink>
                  ),
                )}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

const Wordmark = () => (
  <Link
    to="/"
    data-testid="app-nav-wordmark"
    className="text-[0.9375rem] font-semibold tracking-[-0.02em] text-os-ink"
  >
    Agentic OS
  </Link>
);

export const AppNav = ({ screens }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const current = PORTFOLIO_ITEMS.find((item) => isActivePath(pathname, item));

  return (
    <div
      data-testid="app-nav"
      className="sticky top-0 z-40 border-b border-os-border bg-white font-inter lg:border-b-0"
    >
      {/* The top band: the two live facts (which accounts are cooling, how much
          of their weekly allowance is spent, and the last five days of sales).
          It rides every screen because it lives in the nav. On mobile the
          wordmark moves up into the band; on desktop the band IS the top strip
          and the wordmark stays in the sidebar. */}
      <StatusBand />
      {/* Mobile bar: wordmark moved into the band, so this row keeps the menu
          and the current-page label. */}
      <div className="flex h-12 items-center gap-6 px-4 md:px-6 lg:hidden">
        <button
          type="button"
          data-testid="app-nav-toggle"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((o) => !o)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-control text-os-ink-2 hover:bg-os-wash hover:text-os-ink"
        >
          {open ? (
            <X className="h-5 w-5" strokeWidth={1.75} />
          ) : (
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          )}
        </button>
        <span
          data-testid="topnav-current"
          className="ml-auto text-[0.8125rem] text-os-ink-3"
        >
          {current?.label || ""}
        </span>
      </div>
      {/* Mobile drawer: same menu, one tap away. */}
      {open && (
        <div
          data-testid="app-nav-menu"
          className="border-t border-os-border px-3 py-3 lg:hidden"
        >
          <PortfolioLinks
            pathname={pathname}
            dataPrefix="app-nav-menu-link"
            onNavigate={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

// Desktop ≥1024px: left sidebar carries the menu; the thin top header stays.
export const AppShell = ({ screens }) => {
  const { pathname } = useLocation();
  const [openBrand, setOpenBrand] = useState(null);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [deskFlash, setDeskFlash] = useState(null);

  // Auto-open the brand group whose route is currently active.
  const activeBrandKey = BRAND_GROUPS.find((b) =>
    isBrandGroupActive(pathname, b.key),
  )?.key;

  return (
    // The music provider wraps the WHOLE shell, and the player bar sits
    // BELOW the routed outlet but INSIDE the provider. The audio element
    // therefore lives above the routing: navigating between screens never
    // stops the music.
    <MusicPlayerProvider>
      <div
        data-testid="app-shell"
        className="os-canvas flex min-h-screen font-inter text-os-ink"
      >
        <aside
          data-testid="app-sidebar"
          className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col gap-4 border-r border-os-border bg-white px-3 py-5 lg:flex"
        >
          <Wordmark />
          <PortfolioLinks pathname={pathname} dataPrefix="app-nav-link" />
          <BrandAccordion
            pathname={pathname}
            dataPrefix="app-nav-link"
            openKey={openBrand ?? activeBrandKey}
            setOpenKey={setOpenBrand}
          />
          <p
            data-testid="sidebar-footnote"
            className="mt-auto px-3 font-mono text-[0.625rem] text-os-ink-4"
          >
            One desk for everything Kit runs
          </p>
        </aside>
        <div
          className={
            /* Chat (Jeff 11/04: only the middle chat column scrolls): the chat
               page is a fixed-pane layout (stream pane h-0 flex-1, sidebars
               with their own scrolls), so the shell must NOT stretch — cap it
               to the viewport and clip. Every other screen keeps the natural
               page-scroll document. */
            pathname === "/chat"
              ? "flex h-screen min-w-0 flex-1 flex-col overflow-hidden"
              : "flex min-w-0 flex-1 flex-col"
          }
        >
          <AppNav screens={screens} />
          <main className={pathname === "/chat" ? "min-h-0 min-w-0 flex-1 overflow-hidden" : "min-w-0 flex-1"}>
          <DeskFlashContext.Provider value={[deskFlash, setDeskFlash]}>
            <Outlet />
          </DeskFlashContext.Provider>
          </main>
          <MusicPlayerBar
            capture={
              pathname === "/desk" ? (
                <button
                  type="button"
                  data-testid="idea-capture-fab"
                  aria-label="Add an idea"
                  onClick={() => setIdeaOpen(true)}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full border border-os-border bg-white px-3.5 py-2 font-mono text-[0.8125rem] text-os-ink hover:bg-os-wash focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink"
                >
                  + Idea
                </button>
              ) : null
            }
          />
        </div>
        {ideaOpen && (
          <IdeaCapture onClose={() => setIdeaOpen(false)} desktop />
        )}
      </div>
    </MusicPlayerProvider>
  );
};

// Wall mode for bare screens: canvas only, no nav.
export const WallShell = () => (
  <main
    data-testid="wall-shell"
    className="os-canvas min-h-screen font-inter text-os-ink"
  >
    <Outlet />
  </main>
);
